import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Dialog,
  TextField,
  InputAdornment,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
} from "@mui/material";
import {
  useDeleteCustomerMutation,
  useGetCustomerQuery,
} from "../../../Api/attoDeskApi";
import { ICustomer } from "../../../Api/Interface/api.interface";
import NewPopUpCustomer from "../Edit/index";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate, Link } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import Pagination from "../../../Components/Pagination";
import * as XLSX from "xlsx";

const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
const API_URL = process.env.REACT_APP_API_URL;

// ── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color: string;
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 140,
      bgcolor: "#fff",
      borderRadius: 3,
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      p: 2,
    }}
  >
    <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 24, fontWeight: 700, color, mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const Index = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();

  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [tripsFilter, setTripsFilter] = useState<"" | "yes" | "no">("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<ICustomer | null>(null);
  const [deleteHasRelated, setDeleteHasRelated] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);


  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data, isLoading, isFetching, isError, refetch } = useGetCustomerQuery({
    searchText: currentSearchQuery,
    id: "",
  });

  // true while the debounce hasn't fired yet OR while the API call is in-flight
  const isSearching = isFetching || searchQuery !== currentSearchQuery;

  useEffect(() => {
    if (debouncedSearch.length >= 2 || debouncedSearch === "") {
      setCurrentSearchQuery(debouncedSearch);
      refetch();
    }
  }, [debouncedSearch]);

  const customers: ICustomer[] = Array.isArray(data?.data)
    ? [...(data?.data as ICustomer[])].sort((a, b) => {
        const keyOf = (c: ICustomer) => {
          const fallback = `${c.customerFirstName ?? ""} ${c.customerLastName ?? ""}`.trim();
          return (c.customerCompanyName?.trim() || fallback).toLowerCase();
        };
        return keyOf(a).localeCompare(keyOf(b));
      })
    : [];

  const filtered = useMemo(() => {
    let list = customers;
    if (tripsFilter === "yes") list = list.filter((c) => ((c as any).trip_count || 0) > 0);
    if (tripsFilter === "no")  list = list.filter((c) => ((c as any).trip_count || 0) === 0);
    return list;
  }, [customers, tripsFilter]);

  const totalTrips = customers.reduce((sum, c) => sum + ((c as any).trip_count || 0), 0);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (currentSearchQuery.trim()) params.search = currentSearchQuery.trim();
      if (tripsFilter) params.has_trips = tripsFilter;
      const response = await axios.get(`${API_URL}/pdf/customers-report`, {
        params,
        responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `clients-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err: any) {
      // Decode blob error response to get the real message from the server
      if (err?.response?.data instanceof Blob) {
        const text = await err.response.data.text().catch(() => "");
        const msg = text.match(/"message"\s*:\s*"([^"]+)"/)?.[1] || text.slice(0, 200);
        showErrorMessage(msg || "Failed to generate PDF. Please try again.");
      } else {
        showErrorMessage(err?.response?.data?.message || err?.message || "Failed to generate PDF. Please try again.");
      }
    } finally { setPdfLoading(false); }
  };

  const handleExportExcel = () => {
    const rows = filtered.map((c, i) => ({
      "#": i + 1,
      "Name": fullName(c),
      "Company": c.customerCompanyName || "",
      "Phone": c.customerCompanyPhoneNumebr || c.customerMobileNum || "",
      "Email": c.customerCompanyEmail || c.customerEmail || "",
      "City": c.customerCity || "",
      "Province": c.customerProvince || "",
      "Address": [c.customerStreetNumber, c.customerStreetName].filter(Boolean).join(" "),
      "Postal Code": c.customerPostalCode || "",
      "Trips": (c as any).trip_count || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0] || {}).map((k) => ({
      wch: Math.max(k.length, ...rows.map((r: any) => String(r[k] || "").length)) + 2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clients");
    XLSX.writeFile(wb, `clients_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handlePrint = () => {
    // Open window synchronously inside click handler — browsers won't block it.
    const win = window.open("", "_blank", "width=1100,height=750");
    if (!win) {
      showErrorMessage("Popup blocked. Please allow popups for this site.");
      return;
    }

    // Show a loading state immediately so the window isn't blank.
    win.document.write(`<!DOCTYPE html><html><head><title>Client Report</title>
      <style>
        body { font-family: Arial, sans-serif; display: flex; align-items: center;
               justify-content: center; height: 100vh; margin: 0;
               color: #64748b; font-size: 14px; background: #f8fafc; }
        .spinner { border: 3px solid #e2e8f0; border-top-color: #1e2751;
                   border-radius: 50%; width: 32px; height: 32px;
                   animation: spin 0.8s linear infinite; margin-right: 12px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .msg { display: flex; align-items: center; }
      </style></head>
      <body><div class="msg"><div class="spinner"></div><span>Generating report, please wait…</span></div></body></html>`);
    win.document.close();

    // Build query params from the current UI filters.
    const params = new URLSearchParams();
    if (currentSearchQuery.trim()) params.set("search", currentSearchQuery.trim());
    if (tripsFilter) params.set("has_trips", tripsFilter);

    const token = localStorage.getItem("token") || "";

    fetch(`${API_URL}/pdf/customers-report?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) {
          // Try to extract the real error message the server sent (JSON or text).
          const ct = res.headers.get("content-type") || "";
          let serverMsg = "";
          try {
            if (ct.includes("application/json")) {
              const body = await res.json();
              serverMsg = body?.message || JSON.stringify(body);
            } else {
              serverMsg = (await res.text()).slice(0, 500);
            }
          } catch { /* swallow */ }
          throw new Error(`Server returned ${res.status}${serverMsg ? ": " + serverMsg : ""}`);
        }
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        win.location.href = blobUrl;
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      })
      .catch((err) => {
        const message = err?.message || "Please try again or contact support.";
        if (!win.closed) {
          win.document.open();
          win.document.write(`<!DOCTYPE html><html><body style="font-family:Arial;padding:40px;color:#dc2626;">
            <h3>Failed to generate report</h3>
            <p>${String(message).replace(/[<>&"]/g, (ch) => ({"<":"&lt;",">":"&gt;","&":"&amp;",'"':"&quot;"} as any)[ch])}</p>
            </body></html>`);
          win.document.close();
        }
      });
  };

  const [deleteCustomer] = useDeleteCustomerMutation();

  const handleDelete = async (id: string) => {
    setDeleteLoading(true);
    try {
      const res = await deleteCustomer(id).unwrap();
      if (res.status) {
        showMessage("Client deleted successfully");
        setOpenDeletePopup(false);
        setCustomerToDelete(null);
      } else {
        showErrorMessage(res.message || "Failed to delete the client");
      }
    } catch (err: any) {
      const msg = err?.data?.message || "Failed to delete the client";
      showErrorMessage(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteDialog = (row: ICustomer) => {
    setCustomerToDelete(row);
    setDeleteHasRelated(((row as any).trip_count || 0) > 0);
    setOpenDeletePopup(true);
  };

  const cap = (s: string | undefined) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

  const titleCase = (s: string | undefined | null) =>
    (s ?? "")
      .toLowerCase()
      .replace(/\b([a-z])/g, (m) => m.toUpperCase());

  const fullName = (c: ICustomer) =>
    (c.customerCompanyName ? titleCase(c.customerCompanyName) : "") ||
    [c.customerFirstName, c.customerLastName]
      .filter((s) => s && s.trim() && s.trim().toLowerCase() !== "null")
      .map((s) => titleCase(s!.trim()))
      .join(" ") ||
    "";

  const initials = (c: ICustomer) =>
    (c.customerCompanyName?.[0]?.toUpperCase() || `${c.customerFirstName?.[0] ?? ""}${c.customerLastName?.[0] ?? ""}`.toUpperCase() || "?");

  const cellSx = {
    fontSize: 13,
    color: "#374151",
    borderBottom: "1px solid #f1f5f9",
    py: 1.25,
    px: 2,
  };

  const headCellSx = {
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    bgcolor: "#f8fafc",
    borderBottom: "2px solid #e2e8f0",
    py: 1.25,
    px: 2,
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      {/* ── Page header ───────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40, height: 40, borderRadius: 2,
              background: "linear-gradient(135deg,#2f3891,#2f3891)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <PeopleAltOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>
              Clients
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
              Manage your client directory
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<PersonAddAltOutlinedIcon />}
          onClick={() => navigate("/customer/create")}
          sx={{
            borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13,
            background: "linear-gradient(135deg,#2f3891,#2f3891)",
            boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
            "&:hover": { background: "linear-gradient(135deg,#2f3891,#2f3891)" },
          }}
        >
          Add Client
        </Button>
      </Box>

      {/* ── Stat cards ────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <StatCard label="Total Clients" value={customers.length} color="#1e293b" />
        <StatCard label="Total Trips" value={totalTrips} color="#2f3891" />
      </Box>

      {/* ── Filter bar ────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#fff", borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          px: 2, py: 1.5, mb: 2,
          display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder="Search by name or phone…"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
            endAdornment: isSearching ? (
              <InputAdornment position="end">
                <CircularProgress size={16} sx={{ color: "#2f3891" }} />
              </InputAdornment>
            ) : undefined,
          }}
          sx={{
            width: 260,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2, fontSize: 13, bgcolor: "#f8fafc",
              "&:hover fieldset": { borderColor: "#2f3891" },
              "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: 2 },
            },
          }}
        />

        {/* Trips filter */}
        <Box sx={{ display: "flex", gap: 0.75 }}>
          {(["", "yes", "no"] as const).map((v) => (
            <Chip
              key={v}
              label={v === "" ? "All" : v === "yes" ? "With Trips" : "No Trips"}
              size="small"
              clickable
              onClick={() => { setTripsFilter(v); setPage(0); }}
              sx={{
                fontWeight: 700, fontSize: 11,
                bgcolor: tripsFilter === v ? (v === "yes" ? "#ede9fe" : v === "no" ? "#fef2f2" : "#eef2ff") : "#f1f5f9",
                color: tripsFilter === v ? (v === "yes" ? "#2f3891" : v === "no" ? "#dc2626" : "#2f3891") : "#64748b",
                border: `1px solid ${tripsFilter === v ? (v === "yes" ? "#c7d2fe" : v === "no" ? "#fecaca" : "#c7d2fe") : "#e2e8f0"}`,
              }}
            />
          ))}
        </Box>

        {(searchQuery || currentSearchQuery) && (
          <Button
            size="small"
            onClick={() => { setSearchQuery(""); setCurrentSearchQuery(""); setPage(0); refetch(); }}
            sx={{ textTransform: "none", fontSize: 12, color: "#64748b", borderRadius: 2 }}
          >
            Clear
          </Button>
        )}

        <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
          <Select
            size="small"
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{
              height: 30, fontSize: 12, borderRadius: 2,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
            }}
          >
            <MenuItem value={50}>50 / page</MenuItem>
            <MenuItem value={100}>100 / page</MenuItem>
            <MenuItem value={0}>Show All</MenuItem>
          </Select>
          <Button
            size="small" variant="outlined"
            startIcon={<PrintIcon sx={{ fontSize: 15 }} />}
            onClick={handlePrint}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", borderColor: "#e2e8f0" }}
          >
            Print
          </Button>
          {filtered.length > 0 && (
            <Button
              size="small" variant="outlined"
              startIcon={<TableChartOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={handleExportExcel}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#16a34a", borderColor: "#e2e8f0" }}
            >
              Excel
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Table card ────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#fff", borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {isLoading && !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: "#2f3891" }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>#</TableCell>
                  <TableCell sx={headCellSx}>Client</TableCell>
                  <TableCell sx={headCellSx}>Phone</TableCell>
                  <TableCell sx={headCellSx}>Email</TableCell>
                  <TableCell sx={headCellSx}>Address</TableCell>
                  <TableCell sx={{ ...headCellSx, textAlign: "center" }}>Trips</TableCell>
                  <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isError || filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 13 }}>
                      {isError ? "Error loading clients" : "No clients found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  (rowsPerPage === 0 ? filtered : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage))
                    .map((row, index) => {
                      const trips = (row as any).trip_count || 0;
                      return (
                        <TableRow
                          key={row.id}
                          sx={{ "&:hover": { bgcolor: "#f8fafc" }, transition: "background 0.15s" }}
                        >
                          {/* # */}
                          <TableCell sx={{ ...cellSx, color: "#94a3b8", width: 48 }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>

                          {/* Client name + avatar */}
                          <TableCell sx={cellSx}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              {row.customerImg ? (
                                <Box
                                  component="img"
                                  src={`${LARAVEL_API_URL}/${row.customerImg}`}
                                  alt="avatar"
                                  sx={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                                />
                              ) : (
                                <Avatar
                                  sx={{
                                    width: 34, height: 34, fontSize: 13, fontWeight: 700,
                                    background: "linear-gradient(135deg,#2f3891,#2f3891)",
                                    flexShrink: 0,
                                  }}
                                >
                                  {initials(row)}
                                </Avatar>
                              )}
                              <Box>
                                <Link
                                  to={`/customer-history/${row.id}`}
                                  style={{ textDecoration: "none" }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 13, fontWeight: 600, color: "#1e293b",
                                      "&:hover": { color: "#2f3891" }, lineHeight: 1.3,
                                    }}
                                  >
                                    {fullName(row)}
                                  </Typography>
                                </Link>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Phone */}
                          <TableCell sx={cellSx}>
                            <Typography sx={{ fontSize: 13 }}>
                              {row.customerCompanyPhoneNumebr || row.customerMobileNum || "—"}
                            </Typography>
                          </TableCell>

                          {/* Email */}
                          <TableCell sx={cellSx}>
                            {(() => {
                              const email = row.customerCompanyEmail || row.customerEmail || "";
                              return (
                                <Typography sx={{ fontSize: 13, color: email ? "#374151" : "#cbd5e1" }}>
                                  {email || "—"}
                                </Typography>
                              );
                            })()}
                          </TableCell>

                          {/* Address */}
                          <TableCell sx={cellSx}>
                            {(row.customerStreetNumber || row.customerStreetName || row.customerCity) ? (
                              <Box>
                                {(row.customerStreetNumber || row.customerStreetName) && (
                                  <Typography sx={{ fontSize: 12, color: "#374151" }}>
                                    {[row.customerStreetNumber, row.customerStreetName].filter(Boolean).join(" ")}
                                  </Typography>
                                )}
                                {(row.customerCity || row.customerProvince) && (
                                  <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                    {[row.customerCity, row.customerProvince].filter(Boolean).join(", ")}
                                    {row.customerPostalCode ? ` ${row.customerPostalCode}` : ""}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: 13, color: "#cbd5e1" }}>—</Typography>
                            )}
                          </TableCell>

                          {/* Trips */}
                          <TableCell sx={{ ...cellSx, textAlign: "center" }}>
                            <Chip
                              label={trips}
                              size="small"
                              sx={{
                                fontSize: 12, fontWeight: 700, minWidth: 32,
                                bgcolor: trips > 0 ? "#ede9fe" : "#f1f5f9",
                                color: trips > 0 ? "#2f3891" : "#94a3b8",
                                border: "none",
                              }}
                            />
                          </TableCell>

                          {/* Actions */}
                          <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                              <Tooltip title="Create Quote" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/quote/create/${row.id}`)}
                                  sx={{
                                    color: "#2f3891", bgcolor: "#ede9fe",
                                    borderRadius: 1.5, width: 30, height: 30,
                                    "&:hover": { bgcolor: "#ddd6fe" },
                                  }}
                                >
                                  <NoteAddOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Create Contract Invoice" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/contract-invoice/create/client/${row.id}`)}
                                  sx={{
                                    color: "#0369a1", bgcolor: "#e0f2fe",
                                    borderRadius: 1.5, width: 30, height: 30,
                                    "&:hover": { bgcolor: "#bae6fd" },
                                  }}
                                >
                                  <ArticleOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="View Profile" arrow>
                                <IconButton
                                  size="small"
                                  component={Link}
                                  to={`/customer-history/${row.id}`}
                                  sx={{
                                    color: "#0369a1", bgcolor: "#e0f2fe",
                                    borderRadius: 1.5, width: 30, height: 30,
                                    "&:hover": { bgcolor: "#bae6fd" },
                                  }}
                                >
                                  <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Edit Client" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => { setSelectedCustomer(row); setOpenDialog(true); }}
                                  sx={{
                                    color: "#047857", bgcolor: "#d1fae5",
                                    borderRadius: 1.5, width: 30, height: 30,
                                    "&:hover": { bgcolor: "#a7f3d0" },
                                  }}
                                >
                                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete Client" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => openDeleteDialog(row)}
                                  sx={{
                                    color: "#dc2626", bgcolor: "#fef2f2",
                                    borderRadius: 1.5, width: 30, height: 30,
                                    "&:hover": { bgcolor: "#fecaca" },
                                  }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {filtered.length > 0 && rowsPerPage > 0 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 1.5, borderTop: "1px solid #f1f5f9" }}>
            <Pagination
              count={filtered.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_e: React.MouseEvent<HTMLElement>, p: number) => setPage(p)}
            />
          </Box>
        )}
      </Box>

      {/* ── Dialogs ────────────────────────────────────────────────────────── */}
      {selectedCustomer && (
        <NewPopUpCustomer
          openModel={openDialog}
          handleCloseDialog={() => { setSelectedCustomer(null); setOpenDialog(false); }}
          data={selectedCustomer}
        />
      )}

      {/* ── Delete Confirmation Dialog ──────────────────────────── */}
      <Dialog
        open={openDeletePopup}
        onClose={() => { if (!deleteLoading) { setOpenDeletePopup(false); setCustomerToDelete(null); } }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box sx={{ height: 4, bgcolor: deleteHasRelated ? "#f59e0b" : "#dc2626" }} />
        <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: deleteHasRelated ? "#fffbeb" : "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {deleteHasRelated
                ? <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                : <DeleteOutlineIcon sx={{ fontSize: 18, color: "#dc2626" }} />}
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>
              {deleteHasRelated ? "Cannot Delete Client" : "Confirm Delete"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => { setOpenDeletePopup(false); setCustomerToDelete(null); }} disabled={deleteLoading}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pb: 1 }}>
          {deleteHasRelated ? (
            <Box sx={{ bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 2, px: 2, py: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#92400e", mb: 0.5 }}>
                This client has existing records
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#b45309" }}>
                <strong>{customerToDelete ? fullName(customerToDelete) : ""}</strong> has {(customerToDelete as any)?.trip_count || 0} quote(s) / invoice(s) linked. Clients with existing records cannot be deleted. Please deactivate instead.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: 13, color: "#475569", mb: 1 }}>
                Are you sure you want to delete{" "}
                <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  {customerToDelete ? fullName(customerToDelete) : ""}
                </Box>
                ? This action cannot be undone.
              </Typography>
              <Box sx={{ bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 1.5, px: 1.5, py: 1, mt: 1 }}>
                <Typography sx={{ fontSize: 11, color: "#dc2626" }}>
                  ⚠ The client record will be permanently removed from the system.
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => { setOpenDeletePopup(false); setCustomerToDelete(null); }} disabled={deleteLoading}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}>
            {deleteHasRelated ? "Close" : "Cancel"}
          </Button>
          {!deleteHasRelated && (
            <Button variant="contained" size="small" onClick={() => customerToDelete && handleDelete(customerToDelete.id.toString())} disabled={deleteLoading}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: 1.5, bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" }, boxShadow: "none", minWidth: 90 }}>
              {deleteLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Delete"}
            </Button>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default Index;
